import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MapPin, LocateFixed, ArrowRight, MessageCircle, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { getDistricts } from "@/lib/api";
import { useSettings, waLink } from "@/lib/site";
import {
  getPreferredDistrict, rememberDistrict, nearestDistrict, mentionedDistricts,
} from "@/lib/districtPreference";

const POPULAR = ["cankaya", "kecioren", "golbasi"];

/**
 * Smart district box shown at the end of general (non-district) posts.
 * Picks the reader's district from: saved preference -> districts mentioned in
 * the article -> browser geolocation -> manual select, then recommends the
 * matching district guide + landing page + nearby districts.
 */
export const DistrictRecommender = ({ contentText = "", className = "" }) => {
  const settings = useSettings();
  const { data: districts = [] } = useQuery({ queryKey: ["districts"], queryFn: getDistricts, staleTime: 3600000 });
  const [selected, setSelected] = useState("");
  const [source, setSource] = useState(""); // saved | mentioned | geo | manual
  const [locating, setLocating] = useState(false);

  const mentioned = useMemo(() => mentionedDistricts(districts, contentText).slice(0, 4), [districts, contentText]);

  // initial pick
  useEffect(() => {
    if (!districts.length || selected) return;
    const saved = getPreferredDistrict();
    if (saved && districts.some((d) => d.slug === saved)) {
      setSelected(saved);
      setSource("saved");
      return;
    }
    if (mentioned.length) {
      setSelected(mentioned[0].slug);
      setSource("mentioned");
    }
  }, [districts, mentioned, selected]);

  const current = districts.find((d) => d.slug === selected) || null;
  const suggestions = (mentioned.length ? mentioned : districts.filter((d) => POPULAR.includes(d.slug)))
    .filter((d) => d.slug !== selected)
    .slice(0, 3);
  const nearby = current
    ? (current.nearby || []).map((s) => districts.find((d) => d.slug === s)).filter(Boolean).slice(0, 4)
    : [];

  const choose = (slug, src = "manual") => {
    setSelected(slug);
    setSource(src);
    rememberDistrict(slug);
  };

  const locate = () => {
    if (!navigator.geolocation) {
      toast.error("Tarayıcınız konum özelliğini desteklemiyor");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const best = nearestDistrict(districts, pos.coords.latitude, pos.coords.longitude);
        setLocating(false);
        if (!best) return;
        if (best.km > 120) {
          toast.info(`En yakın ilçe ${best.district.name} (${Math.round(best.km)} km). Ankara dışındaysanız ilçeyi listeden seçebilirsiniz.`);
        } else {
          toast.success(`Konumunuza en yakın ilçe: ${best.district.name}`);
        }
        choose(best.district.slug, "geo");
      },
      () => {
        setLocating(false);
        toast.error("Konum alınamadı. İlçenizi listeden seçebilirsiniz.");
      },
      { timeout: 8000, maximumAge: 600000 }
    );
  };

  if (!districts.length) return null;

  const sourceLabel = {
    saved: "Daha önce ilgilendiğiniz ilçe",
    mentioned: "Bu yazıda geçen ilçe",
    geo: "Konumunuza en yakın ilçe",
    manual: "Seçtiğiniz ilçe",
  }[source];

  return (
    <section className={`rounded-3xl border border-[#C8A24A]/30 bg-[#141517] p-6 sm:p-8 ${className}`} data-testid="district-recommender">
      <div className="flex flex-col lg:flex-row lg:items-start gap-6">
        <div className="lg:w-5/12">
          <span className="eyebrow"><MapPin className="w-4 h-4" /> Size özel ilçe rehberi</span>
          <h2 className="font-display text-2xl sm:text-3xl text-[#F7F2E8] mt-3 leading-tight">
            İlçenize göre çekim mekânlarını keşfedin
          </h2>
          <p className="text-sm text-[#9A9387] mt-3">
            Ankara'nın 25 ilçesi için ayrı rehber hazırladık: en iyi dış çekim noktaları, ışık ve mevsim tavsiyeleri.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 mt-5">
            <Select value={selected} onValueChange={(v) => choose(v, "manual")}>
              <SelectTrigger className="bg-[#0E0F10] border-white/15 text-[#EDE7DA] sm:w-56" data-testid="district-recommender-select" aria-label="İlçe seçin">
                <SelectValue placeholder="İlçenizi seçin" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {districts.map((d) => (
                  <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" onClick={locate} disabled={locating} data-testid="district-recommender-locate"
              className="border-white/20 text-[#F7F2E8] bg-transparent hover:bg-white/5">
              {locating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LocateFixed className="w-4 h-4 mr-2" />}
              Konumumu kullan
            </Button>
          </div>
          {suggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4" data-testid="district-recommender-suggestions">
              <span className="text-xs text-[#7C766B]">{mentioned.length ? "Yazıda geçen:" : "Popüler:"}</span>
              {suggestions.map((d) => (
                <button key={d.slug} type="button" onClick={() => choose(d.slug, mentioned.length ? "mentioned" : "manual")}
                  className="rounded-full border border-white/15 px-3 py-1 text-xs text-[#EDE7DA] hover:border-[#C8A24A]/60 hover:text-white transition-colors"
                  data-testid="district-recommender-chip">
                  {d.name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:w-7/12">
          {current ? (
            <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0E0F10]" data-testid="district-recommender-card">
              <div className="grid sm:grid-cols-5">
                <div className="sm:col-span-2 aspect-[4/3] sm:aspect-auto sm:min-h-[200px]">
                  <img src={current.image} alt={`${current.name} düğün fotoğrafçısı`} loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="sm:col-span-3 p-5">
                  {sourceLabel && <div className="text-[11px] uppercase tracking-[0.14em] text-[#C8A24A]">{sourceLabel}</div>}
                  <h3 className="font-display text-2xl text-[#F7F2E8] mt-1" data-testid="district-recommender-name">{current.name} Düğün Fotoğrafçısı</h3>
                  <p className="text-sm text-[#9A9387] mt-1.5 line-clamp-2">{current.tagline}. {current.spots.slice(0, 3).join(", ")} ve daha fazlası.</p>
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Link to={`/blog/${current.slug}-dugun-fotografcisi`} data-testid="district-recommender-guide-link">
                      <Button size="sm" className="bg-[#C8A24A] text-[#141517] hover:bg-[#D7B86A] font-semibold">
                        <BookOpen className="w-4 h-4 mr-1.5" /> {current.name} rehberini oku
                      </Button>
                    </Link>
                    <Link to={`/ankara/${current.page_slug}`} data-testid="district-recommender-page-link">
                      <Button size="sm" variant="outline" className="border-white/20 text-[#F7F2E8] bg-transparent hover:bg-white/5">
                        İlçe sayfası <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                    <a href={waLink(settings, `Merhaba, ${current.name} için düğün çekimi hakkında bilgi almak istiyorum.`)}
                      target="_blank" rel="noreferrer" data-testid="district-recommender-whatsapp">
                      <Button size="sm" variant="ghost" className="text-[#D7B86A] hover:bg-white/5 hover:text-white">
                        <MessageCircle className="w-4 h-4 mr-1.5" /> WhatsApp
                      </Button>
                    </a>
                  </div>
                  {nearby.length > 0 && (
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-4 text-xs text-[#7C766B]" data-testid="district-recommender-nearby">
                      <span>Yakın ilçeler:</span>
                      {nearby.map((n) => (
                        <button key={n.slug} type="button" onClick={() => choose(n.slug, "manual")} className="text-[#B8B1A5] hover:text-[#D7B86A] underline-offset-2 hover:underline">
                          {n.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[200px] rounded-2xl border border-dashed border-white/15 grid place-items-center text-center p-6" data-testid="district-recommender-empty">
              <div>
                <MapPin className="w-8 h-8 text-[#C8A24A] mx-auto" />
                <p className="text-[#EDE7DA] mt-3">İlçenizi seçin ya da konumunuzu kullanın</p>
                <p className="text-xs text-[#7C766B] mt-1">Size en uygun çekim rotalarını ve rehberi gösterelim.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DistrictRecommender;
